/**
 * What a material is, and everything the cut list needs to measure, group, cut
 * and price it.
 *
 * The seven kinds are not cosmetic: each one decides a different measuring base
 * (volume, area, length, unit), a different grouping key, a different packing
 * engine and a different pricing formula. Nearly every branch in the module
 * eventually turns on `MaterialKind`, so it is defined once, here, and every
 * per-kind rule hangs off a table rather than off scattered conditionals.
 */

import {
  parseLengthList,
  parseSizeList,
  toMillimetres,
  type LengthUnitId,
  type UnitSystem,
} from "../units";

export type MaterialKind =
  | "none"
  | "solid_wood"
  | "panel"
  | "bar"
  | "edge_band"
  | "hardware"
  | "veneer";

/**
 * Presentation order, fixed by 2.1 of the functional spec. Every listing,
 * grouping and estimate section walks the kinds in this order.
 */
export const MATERIAL_KIND_ORDER: readonly MaterialKind[] = [
  "solid_wood",
  "panel",
  "bar",
  "edge_band",
  "veneer",
  "hardware",
  "none",
];

export const MATERIAL_KIND_LABELS: Record<MaterialKind, string> = {
  none: "Sem tipo",
  solid_wood: "Madeira maciça",
  panel: "Painel",
  bar: "Barra ou perfil",
  edge_band: "Fita de borda",
  hardware: "Ferragem e acessório",
  veneer: "Laminado",
};

/** How the material is measured, which is what the estimate divides by. */
export type MeasuringBase = "volume" | "area" | "length" | "unit" | "none";

export const MEASURING_BASE: Record<MaterialKind, MeasuringBase> = {
  none: "none",
  solid_wood: "volume",
  panel: "area",
  bar: "length",
  edge_band: "length",
  hardware: "unit",
  veneer: "area",
};

/** Which packing engine a kind feeds, when it feeds one at all. */
export type PackingDimension = "1d" | "2d" | "none";

export const PACKING_DIMENSION: Record<MaterialKind, PackingDimension> = {
  none: "none",
  solid_wood: "none",
  panel: "2d",
  bar: "1d",
  edge_band: "1d",
  hardware: "none",
  veneer: "2d",
};

/**
 * Edge band and veneer never exist as a modelled component: they are produced
 * by applying a material to an edge or a face of a panel part.
 */
export function isVirtualKind(kind: MaterialKind): boolean {
  return kind === "edge_band" || kind === "veneer";
}

/* -------------------------------------------------------------------------- */
/* Which properties each kind exposes                                          */
/* -------------------------------------------------------------------------- */

export type MaterialProperty =
  | "fixedThickness"
  | "lengthIncrease"
  | "widthIncrease"
  | "thicknessIncrease"
  | "standardLengths"
  | "standardWidths"
  | "standardThicknesses"
  | "standardSections"
  | "standardSizes"
  | "grained"
  | "density"
  | "materialPrice"
  | "cuttingPrice"
  | "edgeDecrement"
  | "estimateOnRaw"
  | "coefficient";

/** Table 2.2 of the functional spec, transcribed once and consulted everywhere. */
const PROPERTY_MATRIX: Record<MaterialKind, readonly MaterialProperty[]> = {
  none: [],
  solid_wood: [
    "lengthIncrease", "widthIncrease", "thicknessIncrease",
    "standardThicknesses", "grained", "density", "materialPrice",
    "estimateOnRaw", "coefficient",
  ],
  panel: [
    "lengthIncrease", "widthIncrease", "standardThicknesses", "standardSizes",
    "grained", "density", "materialPrice", "cuttingPrice", "edgeDecrement",
    "estimateOnRaw", "coefficient",
  ],
  bar: [
    "lengthIncrease", "standardLengths", "standardSections", "grained",
    "density", "materialPrice", "cuttingPrice", "estimateOnRaw", "coefficient",
  ],
  edge_band: [
    "fixedThickness", "lengthIncrease", "widthIncrease", "standardLengths",
    "standardWidths", "grained", "density", "materialPrice", "cuttingPrice",
    "estimateOnRaw", "coefficient",
  ],
  hardware: ["estimateOnRaw", "coefficient"],
  veneer: [
    "fixedThickness", "lengthIncrease", "widthIncrease", "standardSizes",
    "grained", "density", "materialPrice", "cuttingPrice", "estimateOnRaw",
    "coefficient",
  ],
};

export function supportsProperty(kind: MaterialKind, property: MaterialProperty): boolean {
  return PROPERTY_MATRIX[kind].includes(property);
}

export function propertiesOf(kind: MaterialKind): readonly MaterialProperty[] {
  return PROPERTY_MATRIX[kind];
}

/* -------------------------------------------------------------------------- */
/* The material document                                                       */
/* -------------------------------------------------------------------------- */

export interface StandardSize {
  lengthMm: number;
  widthMm: number;
}

/** The unit a price was typed in. The estimate converts to its own base. */
export type PriceUnit =
  | "per_m3"
  | "per_m2"
  | "per_m"
  | "per_unit"
  | "per_stock";

export interface PriceEntry {
  /**
   * Which standard dimension this price belongs to. Empty means the general
   * price, used whenever no specific entry matches.
   *
   * The shape of the key depends on the kind — thickness for solid wood,
   * thickness plus sheet size for panel, section plus bar length for bar — and
   * `priceLookupKeys` is the single place that builds it, most specific first.
   */
  dimensionKey: string;
  price: number;
  unit: PriceUnit;
}

/**
 * How a cutting price was typed. A shop either charges by the metre of blade
 * travel or a flat fee per cut, and the two are not interchangeable: on a sheet
 * broken down into eight strips the per-cut fee is eight times the fee, whatever
 * the sheet measures.
 */
export type CutPriceUnit = "per_m" | "per_ft" | "per_cut";

/**
 * What the saw charges, per standard dimension. Separate from `PriceEntry`
 * because the units are a different set — no area, no volume — and mixing them
 * in one type would let an area price reach the cutting arithmetic.
 */
export interface CutPriceEntry {
  /** Same key space as `PriceEntry.dimensionKey`; empty is the general price. */
  dimensionKey: string;
  price: number;
  unit: CutPriceUnit;
}

export interface DensityEntry {
  dimensionKey: string;
  /** Grams per cubic millimetre once normalised; typed as kg/m³ in the form. */
  densityKgPerM3: number;
}

export interface CutlistMaterial {
  /** Stable identity inside the project. Mirrors `PbrMaterial.id` when it has one. */
  id: string;
  name: string;
  displayName?: string;
  description?: string;
  url?: string;
  /** Swatch colour, used in diagrams and labels. */
  color: string;
  kind: MaterialKind;

  /** Fixed thickness, for the two virtual kinds. */
  thicknessMm: number;

  lengthIncreaseMm: number;
  widthIncreaseMm: number;
  thicknessIncreaseMm: number;

  standardLengthsMm: number[];
  standardWidthsMm: number[];
  standardThicknessesMm: number[];
  standardSectionsMm: StandardSize[];
  standardSizesMm: StandardSize[];

  grained: boolean;
  edgeDecrement: boolean;
  estimateOnRaw: boolean;
  /** Never below 1. Multiplies the raw quantity to represent process loss. */
  coefficient: number;

  prices: PriceEntry[];
  densities: DensityEntry[];
  /**
   * What the saw charges, independent of the material price. A list rather than
   * a single number because the fee follows the stock: breaking down a 2 800 mm
   * sheet is not billed like breaking down a 2 440 mm one, and a shop that
   * charges per cut prices thick stock above thin. Empty means cutting is free.
   */
  cuttingPrices: CutPriceEntry[];

  /** Link to the corporate catalogue, when the material has one (20.2). */
  catalogRef?: { itemId: string; revision?: string };
  /**
   * The material's texture (14.3), when it has one.
   *
   * The physical size is the part that matters and the part most often lost: a
   * texture is an image of a real surface, and a 600 mm wood grain stretched
   * over a 2 440 mm sheet reads as plastic. SketchUp records it, so it comes
   * across on import and stays editable here.
   */
  textureAssetId?: string;
  textureRotationDeg?: number;
  textureWidthMm?: number;
  textureHeightMm?: number;
  textureFileName?: string;
}

/* -------------------------------------------------------------------------- */
/* Factory defaults                                                            */
/* -------------------------------------------------------------------------- */

/**
 * 2.3 of the functional spec, expressed once per unit system. Metric values are
 * written as millimetres; imperial values are written as inch expressions and
 * converted, so the source stays readable as "3/4 inch" rather than 19.05.
 */
interface KindDefaults {
  thicknessMm?: number;
  lengthIncreaseMm?: number;
  widthIncreaseMm?: number;
  thicknessIncreaseMm?: number;
  standardLengthsMm?: number[];
  standardWidthsMm?: number[];
  standardThicknessesMm?: number[];
  standardSectionsMm?: StandardSize[];
  standardSizesMm?: StandardSize[];
  grained?: boolean;
  edgeDecrement?: boolean;
}

const inches = (...values: number[]): number[] => values.map((value) => toMillimetres(value, "in"));
const feet = (...values: number[]): number[] => values.map((value) => toMillimetres(value, "ft"));
const size = (lengthMm: number, widthMm: number): StandardSize => ({ lengthMm, widthMm });
const inchSize = (length: number, width: number): StandardSize =>
  size(toMillimetres(length, "in"), toMillimetres(width, "in"));

const METRIC_DEFAULTS: Record<MaterialKind, KindDefaults> = {
  none: {},
  solid_wood: {
    lengthIncreaseMm: 50,
    widthIncreaseMm: 5,
    thicknessIncreaseMm: 5,
    standardThicknessesMm: [18, 27, 35, 45, 64, 80, 100],
    grained: true,
  },
  panel: {
    standardThicknessesMm: [8, 10, 15, 18, 19, 22],
    standardSizesMm: [size(2_800, 2_070), size(2_500, 1_250), size(2_440, 1_220)],
    grained: true,
  },
  bar: {
    lengthIncreaseMm: 50,
    standardLengthsMm: [2_400, 6_000, 13_000],
    standardSectionsMm: [size(30, 40), size(40, 50)],
    grained: false,
  },
  edge_band: {
    thicknessMm: 2,
    standardLengthsMm: [75_000],
    standardWidthsMm: [19, 23, 28, 33, 35, 43],
    edgeDecrement: true,
    grained: false,
  },
  hardware: {},
  veneer: {
    thicknessMm: 1,
    standardSizesMm: [size(2_800, 2_070)],
    grained: true,
  },
};

/**
 * The imperial column is not a conversion of the metric one — it is a second
 * set of shop conventions, and the reference ships both. A metric shop leaves
 * 50 mm on a length; an imperial shop leaves 1 1/2", which is 38 mm. Converting
 * would have produced numbers no supplier sells.
 */
const IMPERIAL_DEFAULTS: Record<MaterialKind, KindDefaults> = {
  none: {},
  solid_wood: {
    lengthIncreaseMm: toMillimetres(1.5, "in"),
    widthIncreaseMm: toMillimetres(0.25, "in"),
    thicknessIncreaseMm: toMillimetres(0.25, "in"),
    standardThicknessesMm: inches(
      0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6,
    ),
    grained: true,
  },
  panel: {
    standardThicknessesMm: inches(0.25, 0.5, 0.625, 0.75, 1, 1.125),
    standardSizesMm: [inchSize(96, 48), inchSize(47.75, 23.75)],
    grained: true,
  },
  bar: {
    lengthIncreaseMm: toMillimetres(1, "in"),
    standardLengthsMm: feet(6, 8, 10, 12, 14, 16, 18, 20, 22, 24),
    standardSectionsMm: [
      inchSize(1.5, 0.75),
      inchSize(3.5, 1.5),
      inchSize(5.5, 1.5),
      inchSize(7.5, 1.5),
    ],
    grained: false,
  },
  edge_band: {
    thicknessMm: toMillimetres(0.018, "in"),
    standardLengthsMm: feet(250),
    standardWidthsMm: inches(0.625, 0.75, 0.875, 0.9375, 1.3125, 1.625, 1.75),
    edgeDecrement: true,
    grained: false,
  },
  hardware: {},
  veneer: {
    thicknessMm: toMillimetres(0.018, "in"),
    standardSizesMm: [inchSize(96, 48)],
    grained: true,
  },
};

export function factoryDefaults(kind: MaterialKind, system: UnitSystem): KindDefaults {
  return system === "imperial" ? IMPERIAL_DEFAULTS[kind] : METRIC_DEFAULTS[kind];
}

export function createMaterial(
  id: string,
  name: string,
  kind: MaterialKind = "none",
  system: UnitSystem = "metric",
  color = "#B9A07C",
): CutlistMaterial {
  return applyFactoryDefaults(
    {
      id,
      name,
      color,
      kind,
      thicknessMm: 0,
      lengthIncreaseMm: 0,
      widthIncreaseMm: 0,
      thicknessIncreaseMm: 0,
      standardLengthsMm: [],
      standardWidthsMm: [],
      standardThicknessesMm: [],
      standardSectionsMm: [],
      standardSizesMm: [],
      grained: false,
      edgeDecrement: false,
      estimateOnRaw: true,
      coefficient: 1,
      prices: [],
      densities: [],
      cuttingPrices: [],
    },
    system,
  );
}

/**
 * Puts the factory values back for the material's current kind, leaving
 * everything the kind does not own untouched. This is what the "restore
 * defaults" command runs, and it is also how a newly created material is filled.
 */
export function applyFactoryDefaults(
  material: CutlistMaterial,
  system: UnitSystem = "metric",
): CutlistMaterial {
  const defaults = factoryDefaults(material.kind, system);
  return {
    ...material,
    thicknessMm: defaults.thicknessMm ?? 0,
    lengthIncreaseMm: defaults.lengthIncreaseMm ?? 0,
    widthIncreaseMm: defaults.widthIncreaseMm ?? 0,
    thicknessIncreaseMm: defaults.thicknessIncreaseMm ?? 0,
    standardLengthsMm: sortAscending([...(defaults.standardLengthsMm ?? [])]),
    standardWidthsMm: sortAscending([...(defaults.standardWidthsMm ?? [])]),
    standardThicknessesMm: sortAscending([...(defaults.standardThicknessesMm ?? [])]),
    standardSectionsMm: sortSizes((defaults.standardSectionsMm ?? []).map((entry) => ({ ...entry }))),
    standardSizesMm: sortSizes((defaults.standardSizesMm ?? []).map((entry) => ({ ...entry }))),
    grained: defaults.grained ?? false,
    edgeDecrement: defaults.edgeDecrement ?? false,
  };
}

/**
 * Reads a material back from whatever a document or an import supplies, with
 * every field forced into range. A coefficient below 1 is rejected by the form
 * (2.2) but a hand-edited document can still carry one, and a value of 0.4
 * would silently under-price every project that used it.
 */
export function normalizeMaterial(input: Partial<CutlistMaterial> & { id: string }): CutlistMaterial {
  const kind = isMaterialKind(input.kind) ? input.kind : "none";
  const numeric = (value: unknown, fallback = 0): number => {
    const parsed = typeof value === "number" ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  const lengths = (value: unknown): number[] =>
    Array.isArray(value)
      ? value.map((entry) => numeric(entry)).filter((entry) => entry > 0)
      : typeof value === "string"
        ? parseLengthList(value)
        : [];
  const sizes = (value: unknown): StandardSize[] =>
    Array.isArray(value)
      ? value
        .map((entry) => ({
          lengthMm: numeric((entry as StandardSize)?.lengthMm),
          widthMm: numeric((entry as StandardSize)?.widthMm),
        }))
        .filter((entry) => entry.lengthMm > 0 && entry.widthMm > 0)
      : typeof value === "string"
        ? parseSizeList(value)
        : [];
  return {
    id: input.id,
    name: String(input.name ?? input.id),
    displayName: input.displayName,
    description: input.description,
    url: input.url,
    color: typeof input.color === "string" && /^#[0-9a-f]{6}$/i.test(input.color) ? input.color : "#B9A07C",
    kind,
    thicknessMm: Math.max(0, numeric(input.thicknessMm)),
    lengthIncreaseMm: Math.max(0, numeric(input.lengthIncreaseMm)),
    widthIncreaseMm: Math.max(0, numeric(input.widthIncreaseMm)),
    thicknessIncreaseMm: Math.max(0, numeric(input.thicknessIncreaseMm)),
    standardLengthsMm: sortAscending(lengths(input.standardLengthsMm)),
    standardWidthsMm: sortAscending(lengths(input.standardWidthsMm)),
    standardThicknessesMm: sortAscending(lengths(input.standardThicknessesMm)),
    standardSectionsMm: sortSizes(sizes(input.standardSectionsMm)),
    standardSizesMm: sortSizes(sizes(input.standardSizesMm)),
    grained: Boolean(input.grained),
    edgeDecrement: Boolean(input.edgeDecrement),
    estimateOnRaw: input.estimateOnRaw ?? true,
    coefficient: Math.max(1, numeric(input.coefficient, 1)),
    prices: Array.isArray(input.prices)
      ? input.prices
        .filter((entry): entry is PriceEntry => Boolean(entry) && typeof entry.dimensionKey === "string")
        .map((entry) => ({
          dimensionKey: entry.dimensionKey,
          price: Math.max(0, numeric(entry.price)),
          unit: isPriceUnit(entry.unit) ? entry.unit : defaultPriceUnit(kind),
        }))
      : [],
    densities: Array.isArray(input.densities)
      ? input.densities
        .filter((entry): entry is DensityEntry => Boolean(entry) && typeof entry.dimensionKey === "string")
        .map((entry) => ({
          dimensionKey: entry.dimensionKey,
          densityKgPerM3: Math.max(0, numeric(entry.densityKgPerM3)),
        }))
      : [],
    cuttingPrices: normalizeCutPrices(input, numeric),
    catalogRef: input.catalogRef,
    textureAssetId: input.textureAssetId,
    textureRotationDeg: numeric(input.textureRotationDeg),
    // Zero means "unknown", which is how a texture arrives when the source did
    // not record a physical size. The renderer falls back to the sheet size.
    textureWidthMm: Math.max(0, numeric(input.textureWidthMm)),
    textureHeightMm: Math.max(0, numeric(input.textureHeightMm)),
    textureFileName: input.textureFileName,
  };
}

export function isMaterialKind(value: unknown): value is MaterialKind {
  return typeof value === "string" && value in MATERIAL_KIND_LABELS;
}

/**
 * Clears every price the material carries, leaving the geometry alone.
 *
 * A shop asks for this when a supplier's table changes wholesale: the standard
 * dimensions and the allowances stay, only the money goes. Density stays too —
 * it is a property of the material, not of the price list.
 */
export function resetMaterialPrices(material: CutlistMaterial): CutlistMaterial {
  return { ...material, prices: [], cuttingPrices: [] };
}

/**
 * Copies a material under a new identity.
 *
 * Everything is copied, including the texture reference and the catalogue link,
 * because a duplicate exists to be edited from a starting point rather than
 * built again. The caller supplies the new name; a duplicate that silently
 * reused the old one would be indistinguishable in every list.
 */
export function duplicateMaterial(
  material: CutlistMaterial,
  id: string,
  name: string,
): CutlistMaterial {
  return {
    ...material,
    id,
    name,
    displayName: material.displayName ? name : undefined,
    standardLengthsMm: [...material.standardLengthsMm],
    standardWidthsMm: [...material.standardWidthsMm],
    standardThicknessesMm: [...material.standardThicknessesMm],
    standardSectionsMm: material.standardSectionsMm.map((entry) => ({ ...entry })),
    standardSizesMm: material.standardSizesMm.map((entry) => ({ ...entry })),
    prices: material.prices.map((entry) => ({ ...entry })),
    cuttingPrices: material.cuttingPrices.map((entry) => ({ ...entry })),
    densities: material.densities.map((entry) => ({ ...entry })),
  };
}

/**
 * A name that is not taken yet, in the reference's own style: "MDF" becomes
 * "MDF (2)", and a second copy becomes "MDF (3)" rather than "MDF (2) (2)".
 */
export function uniqueMaterialName(
  taken: Iterable<string>,
  base: string,
): string {
  const existing = new Set([...taken].map((entry) => entry.trim().toLocaleLowerCase("pt-BR")));
  const root = base.replace(/\s*\(\d+\)\s*$/, "").trim() || base;
  if (!existing.has(root.toLocaleLowerCase("pt-BR"))) return root;
  for (let counter = 2; counter < 1_000; counter += 1) {
    const candidate = `${root} (${counter})`;
    if (!existing.has(candidate.toLocaleLowerCase("pt-BR"))) return candidate;
  }
  return `${root} (${Date.now()})`;
}

/**
 * The materials nothing in the list uses, which is what "purge unused" removes.
 *
 * A material referenced only as an edge band or a face veneer counts as used —
 * it never appears as a group's own material, and dropping it would silently
 * empty the banding column on the next extraction.
 */
export function unusedMaterialIds(
  materials: Iterable<CutlistMaterial>,
  usedIds: Iterable<string>,
): string[] {
  const used = new Set(usedIds);
  return [...materials].filter((material) => !used.has(material.id)).map((material) => material.id);
}

function isPriceUnit(value: unknown): value is PriceUnit {
  return value === "per_m3" || value === "per_m2" || value === "per_m"
    || value === "per_unit" || value === "per_stock";
}

function isCutPriceUnit(value: unknown): value is CutPriceUnit {
  return value === "per_m" || value === "per_ft" || value === "per_cut";
}

/**
 * Reads the cutting price list, and lifts a document written before the list
 * existed: a single number meant one general price per metre, so that is the
 * entry it becomes. Materials saved with the old field keep quoting the same
 * cutting cost after the upgrade.
 */
function normalizeCutPrices(
  input: Partial<CutlistMaterial> & { cuttingPrice?: unknown },
  numeric: (value: unknown, fallback?: number) => number,
): CutPriceEntry[] {
  if (Array.isArray(input.cuttingPrices)) {
    return input.cuttingPrices
      .filter((entry): entry is CutPriceEntry => Boolean(entry) && typeof entry.dimensionKey === "string")
      .map((entry) => ({
        dimensionKey: entry.dimensionKey,
        price: Math.max(0, numeric(entry.price)),
        unit: isCutPriceUnit(entry.unit) ? entry.unit : "per_m",
      }));
  }
  const legacy = Math.max(0, numeric(input.cuttingPrice));
  return legacy > 0 ? [{ dimensionKey: "", price: legacy, unit: "per_m" }] : [];
}

export function defaultPriceUnit(kind: MaterialKind): PriceUnit {
  switch (MEASURING_BASE[kind]) {
    case "volume": return "per_m3";
    case "area": return "per_m2";
    case "length": return "per_m";
    case "unit": return "per_unit";
    default: return "per_unit";
  }
}

function sortAscending(values: number[]): number[] {
  return [...new Set(values)].sort((a, b) => a - b);
}

function sortSizes(values: StandardSize[]): StandardSize[] {
  const seen = new Set<string>();
  return values
    .filter((entry) => {
      const key = `${entry.lengthMm}x${entry.widthMm}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => a.lengthMm * a.widthMm - b.lengthMm * b.widthMm || a.lengthMm - b.lengthMm);
}

/* -------------------------------------------------------------------------- */
/* Standard dimension selection                                                */
/* -------------------------------------------------------------------------- */

/**
 * The standard dimension a part is bought in — and which of its dimensions the
 * group is keyed by.
 *
 * The reference implementation does **not** use one rule for every kind, and
 * 2.4 of the functional spec generalised the solid-wood rule to all of them.
 * What it actually does:
 *
 * | Tipo | Chave do grupo | Regra |
 * |---|---|---|
 * | Madeira maciça | espessura | a menor espessura padrão que comporta a peça |
 * | Painel | espessura | **igualdade exata**; a espessura do grupo é a da peça |
 * | Barra ou perfil | seção | **igualdade exata** em largura e espessura |
 * | Fita de borda | largura | a menor largura padrão que comporta a fita |
 * | Laminado, ferragem, sem tipo | — | não há dimensão padrão |
 *
 * The difference is not cosmetic. A 15,5 mm panel — the thickness the tenant's
 * whole library is built from — becomes its own group flagged as unavailable,
 * instead of being folded into the 18 mm group and quoted at the wrong price.
 * The flag is the invitation to add 15,5 to the list, which is exactly what
 * "adicionar dimensão padrão a partir do grupo" is for.
 *
 * Neither the sheet size nor the bar length is part of the group: the reference
 * asks for those when the cut plan is run, not when the list is built.
 */
export interface StandardDimension {
  available: boolean;
  thicknessMm?: number;
  lengthMm?: number;
  widthMm?: number;
  section?: StandardSize;
  size?: StandardSize;
}

/** Standard dimensions compare to a part's cutting size, never its final size. */
export interface CuttingSize {
  lengthMm: number;
  widthMm: number;
  thicknessMm: number;
}

const FIT_TOLERANCE_MM = 1e-6;

export function resolveStandardDimension(
  material: CutlistMaterial,
  finished: CuttingSize,
): StandardDimension {
  switch (material.kind) {
    case "solid_wood":
      // The only kind that rounds up: a 23 mm blank is bought as 27 mm stock.
      return resolveThickness(
        material.standardThicknessesMm,
        finished.thicknessMm + material.thicknessIncreaseMm,
        true,
      );
    case "panel":
      // Exact match. A thickness the shop has not declared keeps its own value
      // and the group says so, rather than being rounded onto a sheet the shop
      // does not buy.
      return resolveThickness(
        material.standardThicknessesMm,
        finished.thicknessMm + material.thicknessIncreaseMm,
        false,
      );
    case "bar":
      return resolveSection(material.standardSectionsMm, finished.widthMm, finished.thicknessMm);
    case "edge_band":
      return resolveWidth(material.standardWidthsMm, finished.widthMm);
    default:
      return { available: true };
  }
}

/**
 * The thickness the part is actually cut at.
 *
 * Solid wood is cut from the standard blank it was grouped into, so a 18 mm
 * part with a 5 mm allowance in a shop stocking 18/27/35 is cut from 27 — not
 * from the 23 mm the arithmetic alone suggests. Every other kind keeps the
 * measured thickness plus whatever allowance the material declares.
 */
export function cuttingThicknessFor(
  material: CutlistMaterial,
  finishedThicknessMm: number,
  dimension: StandardDimension,
): number {
  const withIncrease = finishedThicknessMm + material.thicknessIncreaseMm;
  if (material.kind === "solid_wood" && dimension.available && dimension.thicknessMm !== undefined) {
    return dimension.thicknessMm;
  }
  if (material.kind === "edge_band" || material.kind === "veneer") return material.thicknessMm;
  return withIncrease;
}

/**
 * "The smallest declared value that still holds the part" is a statement about
 * the list's contents, not about the order somebody typed it in — so every
 * resolver sorts before it scans. The factory panel list is written largest
 * first, and scanning it as stored would put a 300 mm shelf on a 2800 × 2070
 * sheet.
 */
function ascending(list: readonly number[]): number[] {
  return [...list].sort((left, right) => left - right);
}

/**
 * `nearestHighest` is what separates solid wood from panel. With it, the first
 * standard value at or above the part is taken. Without it, the part keeps its
 * own thickness and only counts as available when it lands exactly on a
 * declared one.
 */
function resolveThickness(
  list: readonly number[],
  thicknessMm: number,
  nearestHighest: boolean,
): StandardDimension {
  if (!list.length) return { available: false, thicknessMm };
  for (const value of ascending(list)) {
    if (thicknessMm > value + FIT_TOLERANCE_MM) continue;
    return nearestHighest
      ? { available: true, thicknessMm: value }
      : { available: Math.abs(value - thicknessMm) <= FIT_TOLERANCE_MM, thicknessMm };
  }
  return { available: false, thicknessMm };
}

function resolveLength(list: readonly number[], lengthMm: number): StandardDimension {
  if (!list.length) return { available: true, lengthMm };
  const sorted = ascending(list);
  const match = sorted.find((value) => value + FIT_TOLERANCE_MM >= lengthMm);
  // A bar longer than the stock length is not a dead end: accumulable parts and
  // 1D packing both split it. The largest declared length is the honest answer,
  // and the group is flagged so the user sees why.
  return match === undefined
    ? { available: false, lengthMm: sorted[sorted.length - 1] }
    : { available: true, lengthMm: match };
}

/** Edge band takes the first declared width that covers the tape. */
function resolveWidth(list: readonly number[], widthMm: number): StandardDimension {
  if (!list.length) return { available: false, widthMm };
  const match = ascending(list).find((value) => value + FIT_TOLERANCE_MM >= widthMm);
  return match === undefined ? { available: false, widthMm } : { available: true, widthMm: match };
}

/**
 * A section has to match exactly, both ways round.
 *
 * 2.4 of the spec describes "the smallest section that holds the part", which
 * would let a 38 × 28 bar be grouped as 40 × 50. The reference does not do
 * that, and it is right not to: a profile is extruded, so a 40 × 50 bar cannot
 * be turned into a 38 × 28 one without machining that no cut plan describes.
 * A section nobody declared keeps its own value and is flagged.
 */
function resolveSection(
  list: readonly StandardSize[],
  widthMm: number,
  thicknessMm: number,
): StandardDimension {
  const own: StandardSize = { lengthMm: widthMm, widthMm: thicknessMm };
  const match = list.find((entry) =>
    Math.abs(entry.lengthMm - widthMm) <= FIT_TOLERANCE_MM
    && Math.abs(entry.widthMm - thicknessMm) <= FIT_TOLERANCE_MM);
  return match
    ? { available: true, section: match }
    : { available: false, section: own };
}

function orderedSizes(list: readonly StandardSize[]): StandardSize[] {
  return [...list].sort((left, right) =>
    left.lengthMm * left.widthMm - right.lengthMm * right.widthMm || left.lengthMm - right.lengthMm);
}

/**
 * A sheet holds the part when the part fits in either orientation. Rotation is
 * allowed here even for a grained material: grouping answers "which sheet do I
 * buy", and the packing engine is what refuses to turn the part.
 */
function resolveSize(
  list: readonly StandardSize[],
  lengthMm: number,
  widthMm: number,
): StandardDimension {
  if (!list.length) return { available: true, size: { lengthMm, widthMm } };
  const match = orderedSizes(list).find((entry) =>
    (entry.lengthMm + FIT_TOLERANCE_MM >= lengthMm && entry.widthMm + FIT_TOLERANCE_MM >= widthMm)
    || (entry.widthMm + FIT_TOLERANCE_MM >= lengthMm && entry.lengthMm + FIT_TOLERANCE_MM >= widthMm));
  return match ? { available: true, size: match } : { available: false, size: { lengthMm, widthMm } };
}

/**
 * The grouping key. Two parts share a group when they share a material and the
 * same standard dimension — 4.2 — so this string is both the group identity and
 * the lookup key for a dimension-specific price.
 */
export function dimensionKeyFor(kind: MaterialKind, dimension: StandardDimension): string {
  const round = (value: number | undefined): string =>
    value === undefined ? "" : String(Math.round(value * 1_000) / 1_000);
  const sizeKey = (entry: StandardSize | undefined): string =>
    entry ? `${round(entry.lengthMm)}x${round(entry.widthMm)}` : "";
  switch (kind) {
    case "solid_wood":
    case "panel":
      return `t:${round(dimension.thicknessMm)}`;
    case "bar": return `x:${sizeKey(dimension.section)}`;
    case "edge_band": return `w:${round(dimension.widthMm)}`;
    // Laminate and hardware carry no standard dimension: one group per material.
    default: return "";
  }
}

/**
 * The part of the key that names the stock rather than the group: the sheet a
 * panel is cut from, the length a bar is bought in, the roll an edge band comes
 * off. Solid wood has none — it is priced by thickness however it was sawn —
 * and neither do the kinds that carry no standard dimension at all.
 */
function stockQualifier(
  kind: MaterialKind,
  stock: { lengthMm: number; widthMm: number } | null | undefined,
): string {
  if (!stock) return "";
  const round = (value: number): string => String(Math.round(value * 1_000) / 1_000);
  switch (kind) {
    case "panel":
    case "veneer":
      return stock.lengthMm > 0 && stock.widthMm > 0
        ? `s:${round(stock.lengthMm)}x${round(stock.widthMm)}`
        : "";
    case "bar":
    case "edge_band":
      return stock.lengthMm > 0 ? `l:${round(stock.lengthMm)}` : "";
    default:
      return "";
  }
}

/**
 * The keys a price, a cutting price or a density is looked up by, most specific
 * first, ending in the empty key that means "general".
 *
 * The reference builds a composite key per kind — thickness for solid wood,
 * thickness *and sheet size* for a panel, section *and bar length* for a bar —
 * then walks it down: exact match, then the first component alone, then the
 * general entry. That middle rung is what makes a price cadastrado por
 * espessura still apply when the plan happens to run on an unusual sheet.
 *
 * Note that the group key and the price key are deliberately different. Two
 * 18 mm panels belong to one group whatever sheet they end up on (4.2), but the
 * sheet is exactly what decides the price, so it enters here and not there.
 */
export function priceLookupKeys(
  kind: MaterialKind,
  dimension: StandardDimension,
  stock?: { lengthMm: number; widthMm: number } | null,
): string[] {
  const group = dimensionKeyFor(kind, dimension);
  const qualifier = stockQualifier(kind, stock);
  const keys: string[] = [];
  if (qualifier) keys.push(group ? `${group}|${qualifier}` : qualifier);
  if (group) keys.push(group);
  keys.push("");
  return keys;
}

/** First entry whose key appears in `keys`, honouring the order of `keys`. */
function firstMatch<T extends { dimensionKey: string }>(
  entries: readonly T[],
  keys: readonly string[],
): T | undefined {
  for (const key of keys) {
    const found = entries.find((entry) => entry.dimensionKey === key);
    if (found) return found;
  }
  return undefined;
}

/** Accepts a single key for callers that only know the group's own dimension. */
function lookupChain(keys: string | readonly string[]): readonly string[] {
  return typeof keys === "string" ? [keys, ""] : keys;
}

/** The price entry that applies, falling back through `priceLookupKeys`. */
export function resolvePrice(
  material: CutlistMaterial,
  keys: string | readonly string[],
): PriceEntry | undefined {
  return firstMatch(material.prices, lookupChain(keys));
}

/**
 * The cutting price that applies. Absent means the saw is free — the reference
 * drops the whole cut line from the estimate rather than showing a zero.
 */
export function resolveCutPrice(
  material: CutlistMaterial,
  keys: string | readonly string[],
): CutPriceEntry | undefined {
  if (!supportsProperty(material.kind, "cuttingPrice")) return undefined;
  return firstMatch(material.cuttingPrices, lookupChain(keys));
}

export function resolveDensity(
  material: CutlistMaterial,
  keys: string | readonly string[],
): number {
  return firstMatch(material.densities, lookupChain(keys))?.densityKgPerM3 ?? 0;
}

/**
 * Every key the material can currently produce, so a form can offer them
 * instead of asking the user to spell `t:18|s:2440x1220` by hand. The general
 * key comes first; the rest follow the standard dimensions in their own order.
 */
export function standardDimensionKeys(material: CutlistMaterial): string[] {
  const keys = new Set<string>([""]);
  const withStock = (group: string): void => {
    keys.add(group);
    const stocks = material.kind === "bar" || material.kind === "edge_band"
      ? material.standardLengthsMm.map((lengthMm) => ({ lengthMm, widthMm: 0 }))
      : material.standardSizesMm;
    for (const stock of stocks) {
      const qualifier = stockQualifier(material.kind, stock);
      if (qualifier) keys.add(group ? `${group}|${qualifier}` : qualifier);
    }
  };
  switch (material.kind) {
    case "solid_wood":
    case "panel":
      for (const thicknessMm of material.standardThicknessesMm) {
        withStock(dimensionKeyFor(material.kind, { available: true, thicknessMm }));
      }
      break;
    case "bar":
      for (const section of material.standardSectionsMm) {
        withStock(dimensionKeyFor("bar", { available: true, section }));
      }
      break;
    case "edge_band":
      for (const widthMm of material.standardWidthsMm) {
        withStock(dimensionKeyFor("edge_band", { available: true, widthMm }));
      }
      break;
    case "veneer":
      withStock("");
      break;
    default:
      break;
  }
  return [...keys];
}

/** Adds a standard dimension to the material so an unavailable group resolves. */
export function addStandardDimension(
  material: CutlistMaterial,
  cutting: CuttingSize,
): CutlistMaterial {
  const next = { ...material };
  const ceilTo = (value: number, step: number): number => Math.ceil(value / step) * step;
  switch (material.kind) {
    case "solid_wood":
      next.standardThicknessesMm = sortAscending([...material.standardThicknessesMm, ceilTo(cutting.thicknessMm, 1)]);
      break;
    case "panel":
      next.standardThicknessesMm = sortAscending([...material.standardThicknessesMm, ceilTo(cutting.thicknessMm, 1)]);
      next.standardSizesMm = sortSizes([
        ...material.standardSizesMm,
        { lengthMm: ceilTo(cutting.lengthMm, 10), widthMm: ceilTo(cutting.widthMm, 10) },
      ]);
      break;
    case "veneer":
      next.standardSizesMm = sortSizes([
        ...material.standardSizesMm,
        { lengthMm: ceilTo(cutting.lengthMm, 10), widthMm: ceilTo(cutting.widthMm, 10) },
      ]);
      break;
    case "bar":
      next.standardSectionsMm = sortSizes([
        ...material.standardSectionsMm,
        { lengthMm: ceilTo(cutting.widthMm, 1), widthMm: ceilTo(cutting.thicknessMm, 1) },
      ]);
      next.standardLengthsMm = sortAscending([...material.standardLengthsMm, ceilTo(cutting.lengthMm, 100)]);
      break;
    case "edge_band":
      next.standardWidthsMm = sortAscending([...material.standardWidthsMm, ceilTo(cutting.widthMm, 1)]);
      next.standardLengthsMm = sortAscending([...material.standardLengthsMm, ceilTo(cutting.lengthMm, 1_000)]);
      break;
    default:
      break;
  }
  return next;
}

/** Human label for a group header, in the module's display unit. */
export function describeStandardDimension(
  kind: MaterialKind,
  dimension: StandardDimension,
  format: (millimetres: number) => string,
): string {
  const sizeText = (entry: StandardSize | undefined): string =>
    entry ? `${format(entry.lengthMm)} × ${format(entry.widthMm)}` : "—";
  switch (kind) {
    case "solid_wood":
    case "panel":
      return dimension.thicknessMm === undefined ? "—" : format(dimension.thicknessMm);
    case "bar": return sizeText(dimension.section);
    case "edge_band": return dimension.widthMm === undefined ? "—" : format(dimension.widthMm);
    default: return "—";
  }
}

/**
 * The stock the packing engines consume for a group.
 *
 * The sheet size and the bar length are *not* group properties — the reference
 * asks for them when the plan is run, and the same group can be planned twice
 * against different stock. The material's first declared size is the default
 * the plan starts from; the caller overrides it.
 */
export function stockForGroup(
  material: CutlistMaterial,
  dimension: StandardDimension,
): { lengthMm: number; widthMm: number } | null {
  switch (PACKING_DIMENSION[material.kind]) {
    case "1d": {
      const lengthMm = material.standardLengthsMm[0];
      return lengthMm
        ? { lengthMm, widthMm: dimension.widthMm ?? material.thicknessMm }
        : null;
    }
    case "2d": {
      const sheet = material.standardSizesMm[0];
      return sheet ? { lengthMm: sheet.lengthMm, widthMm: sheet.widthMm } : null;
    }
    default:
      return null;
  }
}

export function defaultUnitSystem(unit: LengthUnitId): UnitSystem {
  return unit === "in" || unit === "ft" || unit === "yd" ? "imperial" : "metric";
}
